import { useState, useEffect, useMemo } from 'react';
import SideNavbar from "../../../components/SideNavbar";
import { useAppSelector } from "../../../redux/store";
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, where, doc, getDoc, Firestore } from 'firebase/firestore';
import { db, functions as firebaseFunctions } from '../../../firebaseConfig';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format, startOfDay } from 'date-fns';
import { useAuth } from '../../../AuthContext';

// Types for audit logs
interface AuditLog {
  id: string;
  message: string;
  leadUid: string;
  leadName: string;
  userId: string;
  userName: string;
  assignedTo: string;
  assignedToName: string;
  messageType: string;
  timestamp: number;
  details: {
    [key: string]: any;
  }
}

interface UserOption {
  id: string;
  name: string;
  email: string;
}

interface LeadDisplayInfo {
  id: string;
  name: string;
}

type ViewLevel = 'users' | 'leads' | 'trail';

const AuditLogs = () => {
  const isCollapsed = useAppSelector(state => state.sidebar.isCollapsed);
  const { currentUser, userData } = useAuth();

  const [selectedDate, setSelectedDate] = useState<Date>(startOfDay(new Date()));

  const [allCompanyUsers, setAllCompanyUsers] = useState<UserOption[]>([]);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const [errorUsers, setErrorUsers] = useState<string | null>(null);

  const [allLogsForDay, setAllLogsForDay] = useState<AuditLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState<boolean>(false);
  const [errorLogs, setErrorLogs] = useState<string | null>(null);

  const [viewLevel, setViewLevel] = useState<ViewLevel>('users');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedLeadDetails, setSelectedLeadDetails] = useState<LeadDisplayInfo | null>(null);

  const [userSearchQuery, setUserSearchQuery] = useState<string>('');
  const [leadSearchQuery, setLeadSearchQuery] = useState<string>('');
  const [timelineSearchQuery, setTimelineSearchQuery] = useState<string>('');

  // Toast notification function
  const showToast = (message: string, type: 'error' | 'success' | 'info' = 'error') => {
    alert(`${type.toUpperCase()}: ${message}`);
    // In a real app, you'd use a proper toast library here
    if (type === 'error') console.error(message);
  };

  // Fetch all users for the company
  const fetchCompanyUsers = async () => {
    if (!currentUser || !userData) return;
    setLoadingUsers(true);
    setErrorUsers(null);
    try {
      const usersQuery = query(
        collection(db as Firestore, 'users'),
        where('companyId', '==', userData.companyId)
      );
      const usersSnapshot = await getDocs(usersQuery);
      const companyUsers: UserOption[] = [];
      usersSnapshot.forEach(doc => {
        const data = doc.data();
        companyUsers.push({
          id: doc.id,
          name: data.name || data.displayName || 'Unknown User',
          email: data.email || 'No email'
        });
      });
      setAllCompanyUsers(companyUsers.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err: any) {
      console.error('Error fetching company users:', err);
      setErrorUsers(err.message || 'Failed to fetch users');
      showToast(err.message || 'Failed to fetch users', 'error');
    } finally {
      setLoadingUsers(false);
    }
  };

  // Fetch all audit logs for the selected date
  const fetchDailyLogs = async () => {
    if (!selectedDate) return;
    setLoadingLogs(true);
    setErrorLogs(null);
    setAllLogsForDay([]); // Clear previous logs

    try {
      const getAuditLogsCallable = httpsCallable(firebaseFunctions, 'getAuditLogs');
      const formattedDate = format(selectedDate, 'dd-MM-yyyy');
      const result = await getAuditLogsCallable({ date: formattedDate });
      const logsData = result.data as { logs?: AuditLog[] };
      console.log('Fetched logs:', logsData.logs);
      setAllLogsForDay(logsData.logs || []);
    } catch (err: any) {
      console.error('Error fetching daily audit logs:', err);
      setErrorLogs(err.message || 'Failed to fetch audit logs');
      showToast(err.message || 'Failed to fetch audit logs', 'error');
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    fetchCompanyUsers();
  }, [currentUser, userData]);

  useEffect(() => {
    fetchDailyLogs();
    // Reset view when date changes
    setViewLevel('users');
    setSelectedUserId(null);
    setSelectedLeadDetails(null);
  }, [selectedDate]);

  // Memoized calculations for display
  const usersWithLogCounts = useMemo(() => {
    return allCompanyUsers
      .filter(user =>
        userSearchQuery ?
          user.name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
          user.email.toLowerCase().includes(userSearchQuery.toLowerCase())
          : true
      )
      .map(user => ({
        ...user,
        logCount: allLogsForDay.filter(log => log.userId === user.id).length,
        assignedToCount: allLogsForDay.filter(log => log.assignedTo === user.id).length,
      }))
      .sort((a, b) => b.logCount - a.logCount); // Sort by log count descending
  }, [allCompanyUsers, allLogsForDay, userSearchQuery]);

  const leadsForSelectedUser = useMemo(() => {
    if (!selectedUserId) return [];
    // Filter logs where the selected user is assigned to the lead
    const logsByAssignedUser = allLogsForDay.filter(log => log.assignedTo === selectedUserId);

    // Create a map to store leads with their most recent log timestamp
    const leadsMap = new Map<string, LeadDisplayInfo & { latestTimestamp: number }>();

    // Process all logs to find leads and their latest timestamps
    logsByAssignedUser.forEach(log => {
      if (!log.leadUid) return;

      const existingLead = leadsMap.get(log.leadUid);
      if (!existingLead) {
        // First occurrence of this lead
        leadsMap.set(log.leadUid, {
          id: log.leadUid,
          name: log.leadName || 'Unknown Lead',
          latestTimestamp: log.timestamp
        });
      } else if (log.timestamp > existingLead.latestTimestamp) {
        // Update the timestamp if this log is newer
        existingLead.latestTimestamp = log.timestamp;
        leadsMap.set(log.leadUid, existingLead);
      }
    });

    return Array.from(leadsMap.values())
      .filter(lead =>
        leadSearchQuery ?
          lead.name.toLowerCase().includes(leadSearchQuery.toLowerCase())
          : true
      )
      // Sort by most recent log timestamp (descending order)
      .sort((a, b) => b.latestTimestamp - a.latestTimestamp);
  }, [allLogsForDay, selectedUserId, leadSearchQuery]);

  const logsForSelectedLead = useMemo(() => {
    if (!selectedLeadDetails) return [];
    return allLogsForDay
      .filter(log =>
        log.leadUid === selectedLeadDetails.id &&
        (timelineSearchQuery ?
          log.message.toLowerCase().includes(timelineSearchQuery.toLowerCase()) :
          true
        )
      )
      .sort((a, b) => b.timestamp - a.timestamp);
  }, [allLogsForDay, selectedLeadDetails, timelineSearchQuery]);

  useEffect(() => {
    console.log("logs for selected lead:", logsForSelectedLead);
  }
    , [logsForSelectedLead]);


  // Handlers
  const handleUserSelect = (userId: string) => {
    setSelectedUserId(userId);
    setViewLevel('leads');
    setSelectedLeadDetails(null);
    setLeadSearchQuery(''); // Reset lead search
    setTimelineSearchQuery(''); // Reset timeline search
  };

  const handleLeadSelect = (lead: LeadDisplayInfo) => {
    setSelectedLeadDetails(lead);
    setViewLevel('trail');
    setTimelineSearchQuery(''); // Reset timeline search
  };

  const handleBack = () => {
    if (viewLevel === 'trail') {
      setViewLevel('leads');
      setSelectedLeadDetails(null);
      setTimelineSearchQuery('');
    } else if (viewLevel === 'leads') {
      setViewLevel('users');
      setSelectedUserId(null);
      setLeadSearchQuery('');
    }
  };

  // Format timestamps
  const formatTimestamp = (timestamp: number) => {
    try {
      return format(new Date(timestamp), 'hh:mm:ss a');
    } catch (e) {
      return 'Invalid time';
    }
  };

  // Icon and Color utilities
  const getMessageIcon = (messageType: string): JSX.Element => {
    const baseClass = "w-5 h-5";
    switch (messageType) {
      case 'leadCreate':
        return <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`${baseClass} text-green-700`}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
      case 'leadUpdate':
        return <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`${baseClass} text-blue-700`}><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg>;
      case 'leadDelete':
        return <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`${baseClass} text-red-700`}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
      case 'leadReEnquiry':
        return <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`${baseClass} text-purple-700`}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>;
      default:
        return <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`${baseClass} text-gray-700`}><path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" /></svg>;
    }
  };

  const getMessageColor = (messageType: string): string => {
    switch (messageType) {
      case 'leadCreate': return 'text-green-600 bg-green-100';
      case 'leadUpdate': return 'text-blue-600 bg-blue-100';
      case 'leadDelete': return 'text-red-600 bg-red-100';
      case 'leadReEnquiry': return 'text-purple-600 bg-purple-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="min-h-screen bg-gray-200">
      <div className="fixed top-16 left-0 h-full z-10">
        <SideNavbar />
      </div>

      <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-4 md:p-8 transition-all duration-300`}>
        <div className="bg-white p-4 md:p-6 rounded-3xl shadow-md w-full">
          {/* Page Header */}
          <div className="mb-6 md:mb-10">
            <div className="flex items-center gap-4 md:gap-6 mb-4 md:mb-6">
              <div
                className="flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-full"
                style={{ background: 'linear-gradient(180deg, #00A6D9 0%, #3483BE 100%)' }}
              >
                <img
                  className='w-6 h-6 md:w-8 md:h-8'
                  src={`${process.env.PUBLIC_URL}/images/desktop/sidebaricon/audit-logs-white.svg`}
                  alt="Audit Logs Icon"
                />
              </div>
              <h1 className="text-xl md:text-2xl font-medium text-black"
                style={{ fontFamily: 'Montserrat', fontWeight: '500' }}
              >
                Audit Logs
              </h1>
            </div>
            <div className="h-px bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200"></div>
          </div>

          {/* Date Picker and Search Row */}
          <div className="mb-6 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center space-x-2 md:space-x-4">
              <label className="text-sm text-gray-600 whitespace-nowrap">Select Date:</label>
              <DatePicker
                selected={selectedDate}
                onChange={(date: Date | null) => {
                  if (date) {
                    setSelectedDate(startOfDay(date))
                  }
                }}
                dateFormat="dd/MM/yyyy"
                className="w-full md:w-auto p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                popperPlacement="bottom-end"
              />
            </div>

            {/* Universal Search Input Area */}
            <div className="w-full md:w-1/3">
              {viewLevel === 'users' && (
                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  className={`p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 w-full`}
                />
              )}
              {viewLevel === 'leads' && (
                <input
                  type="text"
                  placeholder="Search leads by name..."
                  value={leadSearchQuery}
                  onChange={(e) => setLeadSearchQuery(e.target.value)}
                  className={`p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 w-full`}
                />
              )}
              {viewLevel === 'trail' && (
                <input
                  type="text"
                  placeholder="Search in log trail..."
                  value={timelineSearchQuery}
                  onChange={(e) => setTimelineSearchQuery(e.target.value)}
                  className={`p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 w-full`}
                />
              )}
            </div>
          </div>

          {/* Navigation (Back Button) */}
          <div className="mb-4 flex items-center">
            {viewLevel !== 'users' && (
              <button
                onClick={handleBack}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition self-start"
              >
                &larr; Back
              </button>
            )}
          </div>

          {/* Loading States */}
          {loadingUsers && <p className="text-center text-gray-500">Loading users...</p>}
          {errorUsers && <p className="text-center text-red-500">Error loading users: {errorUsers}</p>}
          {loadingLogs && <p className="text-center text-gray-500">Loading logs for {format(selectedDate, 'dd/MM/yyyy')}...</p>}
          {errorLogs && <p className="text-center text-red-500">Error loading logs: {errorLogs}</p>}

          {/* Content Area based on viewLevel */}
          {!loadingUsers && !errorUsers && !loadingLogs && !errorLogs && (
            <>
              {/* Level 1: Users List */}
              {viewLevel === 'users' && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 3xl:grid-cols-5 gap-4">
                  {usersWithLogCounts.length === 0 && (
                    <div className="text-center py-10">
                      <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}><path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                      <h3 className="mt-2 text-lg font-medium text-gray-700">No User Activity</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {userSearchQuery ? 'Try adjusting your search terms or ' : ''}No user activity found for {format(selectedDate, 'PPP')}.
                      </p>
                    </div>
                  )}
                  {usersWithLogCounts.map(user => (
                    <div
                      key={user.id}
                      onClick={() => handleUserSelect(user.id)}
                      className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 ease-in-out cursor-pointer border border-gray-200 flex items-center justify-between"
                    >
                      <div className="flex-shrink-0 mr-3 flex items-center justify-center w-9 h-9">
                        <img src="/images/desktop/AuditUser.svg" alt="User Icon" className="w-9 h-9" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className='font-medium text-gray-800 block'>{user.name}</span>
                        <span className='text-xs text-gray-500 block'>{user.email}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                      <div className="flex flex-col items-center space-y-2">
                        <span className="px-3 py-1 text-xs font-semibold text-blue-700 bg-blue-100 rounded-full">{user.logCount} logs by user</span>
                        <span className="px-3 py-1 text-xs font-semibold text-green-700 bg-green-100 rounded-full">{user.assignedToCount} logs assigned</span>
                      </div>
                        <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Level 2: Leads List for Selected User */}
              {viewLevel === 'leads' && selectedUserId && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {leadsForSelectedUser.length === 0 && (
                    <div className="text-center py-10">
                      <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}><path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                      <h3 className="mt-2 text-lg font-medium text-gray-700">No Leads Found</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {leadSearchQuery ? 'Try adjusting your search terms or ' : ''}No leads found for this user on {format(selectedDate, 'PPP')}.
                      </p>
                    </div>
                  )}
                  {leadsForSelectedUser.map(lead => (
                    <div
                      key={lead.id}
                      onClick={() => handleLeadSelect(lead)}
                      className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 ease-in-out cursor-pointer border border-gray-200 flex items-center justify-between"
                    >
                      <div className="flex-shrink-0 mr-3 flex items-center justify-center w-9 h-9">
                        <img src="/images/desktop/AuditLead.svg" alt="Lead Icon" className="w-9 h-9" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className='font-medium text-gray-800'>{lead.name}</span>
                      </div>
                      <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                    </div>
                  ))}
                </div>
              )}

              {/* Level 3: Log Trail for Selected Lead */}
              {viewLevel === 'trail' && selectedLeadDetails && (
                <>
                  {logsForSelectedLead.length === 0 ? (
                    <div className="text-center py-10">
                      <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <h3 className="mt-2 text-lg font-medium text-gray-700">No Audit Logs</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {timelineSearchQuery ? 'Try adjusting your search terms or ' : `No activity for ${selectedLeadDetails.name} on ${format(selectedDate, 'PPP')}.`}
                      </p>
                    </div>
                  ) : (
                    <div className="mt-6">
                      <h3 className="mb-1 text-lg font-semibold text-gray-800">Log Trail for: <span className="text-blue-600">{selectedLeadDetails.name}</span></h3>
                      <p className="mb-4 text-gray-600">Showing all activity for this lead</p>
                      {/* <div className="border-l-2 border-gray-200 ml-1 md:ml-2relative"> */}
                      <div className="p-3 xl:w-[40%] lg:w-[60%] md:w-[70%] sm:w-[90%] mx-auto">
                        <div className="relative">
                          <div className="absolute left-1/2 top-0 h-full w-px bg-blue-400" />
                          <div className="space-y-3">
                            {logsForSelectedLead.map((log, index) => (
                              <div key={log.id} className={`relative flex ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                                <div className="absolute left-1/2 top-0 transform -translate-x-1/2">
                                  <div className="w-2 h-2 rounded-full bg-white border-2 border-blue-400" />
                                </div>

                                <div className={`w-1/2 ${index % 2 === 0 ? 'pr-4' : 'pl-4'}`}>

                                  {/* Icon and timestamp container with proper ordering */}
                                  <div
                                    className={`flex items-center gap-2 ${index % 2 === 0 ? 'justify-end' : 'justify-start'
                                      }`}
                                  >
                                    {/* For left-aligned messages, show icon first */}
                                    {index % 2 !== 0 && (
                                      <div className={`w-7 h-7 rounded-full ring-2 ring-white flex items-center justify-center ${getMessageColor(log.messageType).split(' ')[1]}`}>
                                        {getMessageIcon(log.messageType)}
                                      </div>
                                    )}

                                    <div className="text-xs text-gray-500">
                                      <time className="text-xs text-gray-500 whitespace-nowrap">{format(new Date(log.timestamp), 'p, MMM d')}</time>
                                    </div>

                                    {/* For right-aligned messages, show icon last */}
                                    {index % 2 === 0 && (
                                      <div className={`w-7 h-7 rounded-full ring-2 ring-white flex items-center justify-center ${getMessageColor(log.messageType).split(' ')[1]}`}>
                                        {getMessageIcon(log.messageType)}
                                      </div>
                                    )}
                                  </div>


                                  <div className="mt-0.5 bg-white rounded-lg border border-gray-200 p-2 hover:shadow-sm transition-shadow">
                                    <div className={`${index % 2 === 0 ? 'text-right' : 'text-left'}`}>
                                      <h3 className="font-medium text-xs text-gray-900">
                                        <span className={`font-semibold ${getMessageColor(log.messageType).split(' ')[0]}`}>{log.message}</span>
                                      </h3>
                                      <div className="text-xs text-gray-500 mt-0.5">
                                        <div className="text-sm text-gray-600">
                                          <p>By: {log.userName === 'SYSTEM' ? <span className="font-medium text-indigo-600">SYSTEM</span> : log.userName}</p>
                                          <p>Assigned to: {log.assignedToName === 'Unassigned' ? <span className="italic text-gray-500">Unassigned</span> : log.assignedToName}</p>
                                          {log?.details &&
                                            <p>
                                              From <span className="font-medium text-red-400">{log?.details?.from !== '' ? log?.details?.from : 'NILL'}</span>
                                              <span className="font-medium text-gray-500"> </span>
                                              to <span className="font-medium text-green-400">{log?.details?.to}</span>
                                            </p>}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditLogs;
